<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">
          Jobs
        </h1>
      </template>
      <template #actions>
        <page-header-action tag="router-link" to="jobs/create">
          New Job
        </page-header-action>
      </template>
    </page-header>

    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <div class="loading-overlay" v-if="loading"></div>
          <div class="card-header">
            <div class="clearfix" style="width: 100%;">
              <div class="row">
                <div class="col-md-3 pb-2">
                  <datepicker-input
                    :value="filters.date"
                    @input="setDate"
                    label="Date"
                    :stacked="true"
                  ></datepicker-input>
                </div>
                <div class="col-md-3 pb-2">
                  <selectlist
                    :value="filters.type"
                    @input="setType"
                    name="type"
                    label="Type"
                    :stacked="true"
                    :options="jobTypes"
                  ></selectlist>
                </div>
                <div class="col-md-3 pb-2">
                  <selectlist
                    :value="filters.assigned"
                    @input="setAssignedStatus"
                    name="assigned"
                    label="Assigned"
                    :stacked="true"
                    :options="assignedStatuses"
                  ></selectlist>
                </div>
                <div class="col-md-3 pb-2">
                  <selectlist
                    :value="filters.docusign_status"
                    @input="setDocusignStatus"
                    name="docusignStatus"
                    label="Docusign Status"
                    :stacked="true"
                    :options="docusignStatuses"
                  ></selectlist>
                </div>
                <div class="col-md-3 pb-2">
                  <selectlist
                    :value="filters.complete"
                    @input="setStatus"
                    name="status"
                    label="Status"
                    :stacked="true"
                    :options="statuses"
                  ></selectlist>
                </div>
                <div class="col pb-2">
                  <label class="control-label">Search:</label>
                  <input
                    name="terms"
                    type="text"
                    class="form-control"
                    :value="filters.terms"
                    @input="setTerms($event.target.value)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Claim Reference</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Location</th>
                  <th>Vehicle</th>
                  <th>Description</th>
                  <th>Assigned To Run?</th>
                  <th>Docusign Status</th>
                  <th
                    style="cursor: pointer; user-select: none;"
                    @click="toggleSort('requested_date')"
                  >
                    Requested Date
                    <span v-if="sort === 'requested_date'">&#8593;</span>
                    <span v-else-if="sort === '-requested_date'">&#8595;</span>
                  </th>
                  <th
                    style="cursor: pointer; user-select: none;"
                    @click="toggleSort('deployed_date')"
                  >
                    Deployed Date
                    <span v-if="sort === 'deployed_date'">&#8593;</span>
                    <span v-else-if="sort === '-deployed_date'">&#8595;</span>
                  </th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody v-if="!!jobs && jobs.length">
                <tr v-bind:key="index" v-for="(job, index) in jobs">
                  <td>
                    <jobable-claim-reference
                      :job="job"
                    ></jobable-claim-reference>
                  </td>
                  <td>
                    <job-label :job="job"></job-label>
                  </td>
                  <td>
                    {{ job.date | date }}
                  </td>
                  <td style="white-space: pre-line">
                    {{ job.location | location }}
                  </td>
                  <td>
                    {{ job.vehicle?.registration }}
                  </td>
                  <td>
                    {{ job.description }}
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="{
                        'badge-success': job.item,
                        'badge-danger': !job.item
                      }"
                      v-if="job.type?.name !== 'third_party_movement'"
                    >
                      {{ job.item ? 'Yes' : 'No' }}
                    </span>
                    <span v-else>N/A</span>
                  </td>
                  <td>
                    <envelope-status-badge
                      :job="job"
                      :status="job.envelopes[0]?.status"
                    />
                  </td>
                  <td>
                    <span v-if="job.requested_date">{{ job.requested_date | date('DD/MM/YYYY HH:mm') }}</span>
                    <span v-else>N/A</span>
                  </td>
                  <td>
                    <span v-if="job.deployed_date">{{ job.deployed_date | date('DD/MM/YYYY HH:mm') }}</span>
                    <span v-else>N/A</span>
                  </td>
                  <td class="text-right text-nowrap">
                    <router-link
                      class="btn btn-primary"
                      :to="`/jobs/${job.identifier}`"
                    >
                      View
                    </router-link>
                    <button
                      class="btn btn-danger"
                      v-on:click.prevent="deleteJob(job)"
                      v-if="hasPermission('jobs.delete')"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="!jobs.length">
                <tr>
                  <td colspan="11" class="text-center">
                    No results found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-footer text-right" v-show="showPagination">
            <pagination
              :data="paginator_data"
              v-on:updateResults="getData"
            ></pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JobLabel from '@/components/Modules/Shared/JobLabel';
import JobableClaimReference from '@/components/Modules/Job/Children/JobableClaimReference';
import debounce from 'lodash/debounce';
import EnvelopeStatusBadge from '@/components/Modules/Shared/EnvelopeStatusBadge';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    JobLabel,
    JobableClaimReference,
    EnvelopeStatusBadge,
    PageHeader,
    PageHeaderAction
  },

  data() {
    return {
      loading: false,
      jobs: [],
      paginator_data: {},
      filters: {
        type: null,
        driver_id: null,
        date: null,
        complete: 2,
        assigned: 2,
        terms: '',
        docusign_status: ''
      },
      archiveIndicator: false,
      sort: 'date'
    };
  },

  computed: {
    showPagination() {
      return (
        this.paginator_data.next_page_url || this.paginator_data.prev_page_url
      );
    },
    jobTypes() {
      return this.$store.getters['JobTypes/all'];
    },
    statuses() {
      return [
        { id: 1, label: 'Complete' },
        { id: 2, label: 'Incomplete' }
      ];
    },
    assignedStatuses() {
      return [
        { id: 1, label: 'Yes' },
        { id: 2, label: 'No' }
      ];
    },
    docusignStatuses() {
      return [
        { id: 'not_sent', label: 'Not Sent' },
        { id: 'sent', label: 'Sent' },
        { id: 'signed', label: 'Signed' }
      ];
    }
  },

  filters: {
    location(value) {
      if (typeof value === 'string') {
        return value.replace(/,/g, '\n');
      }

      return value;
    }
  },

  mounted() {
    this.$store.commit('JobTypes/retrieve');

    this.getJobs();
  },

  methods: {
    getJobs() {
      this.getData(`/jobs?per_page=${this.perPage}`);
    },

    getData(url) {
      Object.keys(this.filters).forEach(key => {
        let value = this.filters[key];

        if (key === 'date' && value) {
          value = window.DateManager.format(value, 'YYYY-MM-DD');
        }

        url = `${url}&filter[${key}]=${value || ''}`;
      });

      this.loading = true;

      window.Ajax.get(`${url}&sort=${this.sort}&parts=item,type,jobable`)
        .then(response => {
          this.jobs = response.data.data;
          this.paginator_data = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
        });
    },

    setDate(date) {
      this.filters.date = date;

      this.getJobs();
    },

    setType(type) {
      this.filters.type = type;

      this.getJobs();
    },

    setStatus(status) {
      this.filters.complete = status;

      this.getJobs();
    },

    setAssignedStatus(status) {
      this.filters.assigned = status;

      this.getJobs();
    },

    setDocusignStatus(status) {
      this.filters.docusign_status = status;

      this.getJobs();
    },

    setTerms: debounce(function(terms) {
      this.filters.terms = terms;

      this.getJobs();
    }, 400),

    toggleSort(field) {
      this.sort = this.sort === field ? `-${field}` : field;
      this.getJobs();
    },

    deleteJob(job) {
      window
        .swal({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete the job!'
        })
        .then(() => {
          window.Ajax.delete(`/jobs/${job.identifier}`)
            .then(response => {
              window.toastify.success('Success', 'The job was deleted.');

              const key = this.jobs.indexOf(job);

              this.jobs.splice(key, 1);
            })
            .catch(err => {
              window.toastify.error(
                'Error',
                'Sorry, there was an issue deleting the job.'
              );
            });
        });
    }
  }
};
</script>
